// eslint-disable-next-line @altermaind/you-shall-not-pass
import { useContext, useEffect, useMemo } from 'react';
import { useTranslations } from '@altermaind/core/i18n';
import { Box } from '@altermaind/web/ui';
import { ClaimsComponent, GoToPublicsDocs, Tabs, TutorialDialog } from '@/_shared/components';
import { LayoutContext } from '@/_shared/components/PageLayout/LayoutContext';
import { TutorialKeys } from '@/_shared/constants';
import { Claims } from '@/_shared/constants/claims';
import { FeatureFlag } from '@/_shared/constants/featureFlags';
import { Roles } from '@/_shared/constants/roles';
import { useTutorialCheck } from '@/_shared/hooks';
import { useDialog } from '@/_shared/hooks/useDialog';
import { useFeatureFlags } from '@/_shared/hooks/useFeatureFlag';
import { useResponsiveStyle } from '@/_shared/hooks/useResponsiveStyle';
import { useTabs } from '@/_shared/hooks/useTabs';
import { AspectTab, CreditsTab, DelegatesTab, PlanTab } from '../components';

type TabKey = 'plan' | 'delegates' | 'credits' | 'aspect';

export const SettingsScreen = () => {
  // eslint-disable-next-line @altermaind/use-translations-scope
  const t = useTranslations('settings');
  const { isFeatureEnabled } = useFeatureFlags();
  const { genericResponsiveValue } = useResponsiveStyle();
  const organizationFF = isFeatureEnabled(FeatureFlag.ORGANIZATION);
  const creditsFF = isFeatureEnabled(FeatureFlag.CREDITS);
  const aspectFF = isFeatureEnabled(FeatureFlag.ASPECT);

  const { openDialog: openDialogSettingsTutorial } = useDialog('settingsTutorial');
  const { setLayout } = useContext(LayoutContext);

  const { hasAlreadySeenTutorial } = useTutorialCheck();

  const visibleTabs = useMemo(() => {
    const list: { key: TabKey; title: string }[] = [];
    if (organizationFF) {
      list.push({ key: 'plan', title: t('tabs.plan.title') });
      list.push({ key: 'delegates', title: t('tabs.delegates.title') });
    }
    if (organizationFF && creditsFF) {
      list.push({ key: 'credits', title: t('tabs.credits.title') });
    }
    if (aspectFF) {
      list.push({ key: 'aspect', title: t('tabs.aspect.tabTitle') });
    }
    return list;
  }, [organizationFF, creditsFF, aspectFF, t]);

  const tabsInitialState = visibleTabs.map((tab, index) => ({
    title: tab.title,
    index,
    active: index === 0,
    dataTestId: tab.key,
  }));

  const { tabs, setOpenTab } = useTabs({ tabsInitialState });

  const slides = [
    {
      title: t('tutorial.slides.slide1.title'),
      description: t('tutorial.slides.slide1.description'),
      assetName: 'tutorialSettingPlanSlide1',
    },
    {
      title: t('tutorial.slides.slide2.title'),
      description: t('tutorial.slides.slide2.description'),
      assetName: 'tutorialOrganizationSlide1',
    },
    {
      title: t('tutorial.slides.slide3.title'),
      description: t('tutorial.slides.slide3.description'),
      assetName: 'tutorialOrganizationSlide3',
    },
    ...(creditsFF
      ? [
          {
            title: t('tutorial.slides.slide4.title'),
            description: t('tutorial.slides.slide4.description'),
            assetName: 'tutorialOrganizationSlide4',
          },
        ]
      : []),
    {
      title: t('tutorial.slides.slide5.title'),
      description: t('tutorial.slides.slide5.description'),
      assetName: 'tutorialAspectSlide1',
    },
  ];

  useEffect(() => {
    if (!hasAlreadySeenTutorial(TutorialKeys.Settings)) {
      openDialogSettingsTutorial();
    }
  }, [hasAlreadySeenTutorial, openDialogSettingsTutorial]);

  useEffect(() => {
    setLayout({ title: 'settings' });
  }, [setLayout, t]);

  return (
    <Box>
      <Box>
        <Box
          display='flex'
          flexDirection={genericResponsiveValue({
            defaultValue: 'row',
            breakPoints: {
              mobile: 'column-reverse',
            },
          })}
          alignItems='center'
          justifyContent='space-between'
        >
          <Box flex={1} overflowX='auto' maxWidth='100%'>
            <Tabs buttonVariant='primary' setActiveTab={setOpenTab} tabs={tabs} />
          </Box>
          <Box
            display='flex'
            justifyContent='flex-end'
            width={genericResponsiveValue({
              defaultValue: 'auto',
              breakPoints: {
                mobile: '100%',
              },
            })}
          >
            <GoToPublicsDocs mb='none' />
          </Box>
        </Box>
        {tabs.map(tab => {
          if (!tab.active) {
            return null;
          }
          const tabKey = visibleTabs[tab.index]?.key;
          return (
            <Box key={tab.title} display='flex' flexDirection='column'>
              {tabKey === 'plan' && (
                <ClaimsComponent
                  featureFlag={FeatureFlag.ORGANIZATION}
                  claimKey={Claims.ORGANIZATION_READ}
                  roles={[Roles.Admin, Roles.HR_Delegate]}
                >
                  <PlanTab />
                </ClaimsComponent>
              )}
              {tabKey === 'delegates' && (
                <ClaimsComponent
                  featureFlag={FeatureFlag.ORGANIZATION}
                  claimKey={Claims.ORGANIZATION_READ}
                  roles={[Roles.Admin, Roles.HR_Delegate]}
                >
                  <DelegatesTab />
                </ClaimsComponent>
              )}
              {tabKey === 'credits' && (
                <ClaimsComponent
                  featureFlag={FeatureFlag.CREDITS}
                  claimKey={Claims.ORGANIZATION_READ}
                  roles={[Roles.Admin, Roles.HR_Delegate]}
                >
                  <CreditsTab />
                </ClaimsComponent>
              )}
              {tabKey === 'aspect' && (
                <ClaimsComponent
                  featureFlag={FeatureFlag.ASPECT}
                  claimKey={Claims.ASPECT_READ}
                  roles={[Roles.Admin, Roles.HR_Delegate]}
                >
                  <AspectTab />
                </ClaimsComponent>
              )}
            </Box>
          );
        })}
      </Box>
      <TutorialDialog
        translationsKey='settings.tutorial'
        dialogKey='settingsTutorial'
        slides={slides}
      />
    </Box>
  );
};
